import React, { useContext } from "react";
import { Outlet } from "react-router-dom";
import {
  AppBar,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  CssBaseline,
  Divider,
} from "@mui/material";
import { Logout } from "@mui/icons-material";
import { ReactComponent as IconRead } from "../assets/sidebar-icon-read.svg";
import { ReactComponent as IconConfirm } from "../assets/sidebar-icon-confirm.svg";
import { ReactComponent as IconExport } from "../assets/sidebar-icon-export.svg";

import { AuthContext } from "../contexts/AuthContext";
import cozciLogo from "../assets/cozci-upload-logo.svg";

const drawerWidth = 260;

interface DashboardLayoutProps {
  handleNavigate: (path: string) => void;
  handleLogout: () => void;
}

interface MenuItem {
  text: string;
  path: string;
  rightIcon: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  handleNavigate,
  handleLogout,
}) => {
  const { isAdmin } = useContext(AuthContext)!;

  const adminMenuItems: MenuItem[] = [
    // { text: "ダッシュボード", path: "/admin/dashboard", rightIcon: <Dashboard sx={{ color: 'white', fontSize: 40 }} /> },
  ];

  const userMenuItems: MenuItem[] = [
    { text: "読み込む", path: "/", rightIcon: <IconRead /> },
    { text: "確認する", path: "/confirm", rightIcon: <IconConfirm /> },
    { text: "出力する", path: "/export", rightIcon: <IconExport /> },
  ];

  const menuItems = isAdmin ? adminMenuItems : userMenuItems;

  const buttonStyles: { [key: string]: { gradient: string; shadow: string } } =
    {
      読み込む: {
        gradient:
          "linear-gradient(to bottom, rgb(255, 89, 127), rgb(255, 138, 165))",
        shadow: "0 6px 15px rgba(255, 89, 127, 0.4)",
      },
      確認する: {
        gradient:
          "linear-gradient(to bottom, rgb(194, 140, 255), rgb(211, 172, 255))",
        shadow: "0 6px 15px rgba(194, 140, 255, 0.4)",
      },
      出力する: {
        gradient:
          "linear-gradient(to bottom, rgb(50, 225, 170), rgb(109, 234, 195))",
        shadow: "0 6px 15px rgba(50, 225, 170, 0.4)",
      },
    };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "transparent",
          color: "#000",
          boxShadow: "none",
        }}
      >
        <Toolbar
          sx={{
            minHeight: "90px",
            maxHeight: "90px",
            height: "90px",
            overflow: "hidden",
            display: "flex",
            justifyContent: "flex-start", // 左寄せ
            alignItems: "center", // 垂直方向は中央
          }}
        >
          <img src={cozciLogo} alt="COZCI Logo" className="dashboard-logo" />
        </Toolbar>
        <Box
          sx={{
            height: "4px",
            width: "100%",
            background:
              "linear-gradient(to right, rgb(204, 109, 247), rgb(140, 255, 140),rgb(251, 255, 140))",
          }}
        />
      </AppBar>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: "border-box",
            backgroundColor: "transparent",
            borderRight: "none",
          },
        }}
      >
        <Toolbar sx={{ minHeight: "80px" }} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            pt: 2,
            pb: 2,
            pr: 2,
            pl: 0,
          }}
        >
          <List sx={{ mt: "2rem" }}>
            {menuItems.map((item) => {
              const styles = buttonStyles[item.text];

              return (
                <ListItem key={item.text} disablePadding sx={{ mb: 2 }}>
                  <ListItemButton
                    onClick={() => handleNavigate(item.path)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      borderRadius: "12px 50px 50px 12px",
                      border: "6px solid rgba(240, 232, 249, 0.9)",
                      transition:
                        "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                      color: "#fff",
                      background: styles.gradient,
                      boxShadow: styles.shadow,
                      "&:hover": {
                        transform: "scale(1.1)",
                        boxShadow: "0 8px 20px rgba(0, 0, 0, 0.25)",
                      },
                      "& .MuiListItemIcon-root": {
                        color: "#fff",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        pl: "2rem",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontWeight: "bold",
                          fontSize: "20px",
                        }}
                      />
                      <ListItemIcon>{item.rightIcon}</ListItemIcon>
                    </Box>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
          <Box sx={{ flexGrow: 1 }} />
          <Divider />
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={handleLogout}>
                <ListItemIcon>
                  <Logout />
                </ListItemIcon>
                <ListItemText primary="ログアウト" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          overflowX: "auto", // Allow horizontal scrolling for the main content area
          overflowY: "auto", // Keep vertical scrolling for main content
        }}
      >
        <Toolbar sx={{ minHeight: "80px" }} />
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            overflow: "auto",
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardLayout;
